import React, { useState, useEffect } from "react";
import { Eye, EyeOff } from "lucide-react";
import MessageAlert from "../../components/Authentication/MessageAlert";
import OAuthButton from "../../components/Authentication/OAuthButton";
import AuthPageLayout from "../../components/Authentication/AuthPageLayout";
import { AcceptedInviteIcon } from "../../assets";
import { Navigate, useLocation } from "react-router-dom";
import { loginWithFirebase } from "../../api/auth";
import UserAuthStore from "../../store/userStore";
import authService from "../../services/AuthService";
import { useTheme } from "../../context/ThemeProvider";
import Loader from "../../components/Loader/Loader";
import useFeedback from "../../hooks/useFeedback";
import { getModeAuth } from "../../utils/sparkApi/getModeAuth";
import logger from "../../services/Logger";
import { persistAuthSession } from "../../services/AuthSession";

const BROWSER_AUTH_RETURN_KEY = "photon_browser_authorization_return";

const Login = () => {
  const [loading, setLoading] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [nameInput, setNameInput] = useState("");
  const [resetEmailError, setResetEmailError] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [usePasswordless, setUsePasswordless] = useState(false);
  const [linkSubmitted, setLinkSubmitted] = useState(false);
  const [linkSending, setLinkSending] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [ssoProviders, setSsoProviders] = useState([]);
  // Set when a valid sign-in-link URL is opened but "emailForSignIn" isn't in
  // localStorage (e.g. the link was opened on a different device/browser than
  // the one that requested it) — prompts the user for their email instead of
  // dead-ending with just an error toast.
  const [awaitingEmailForLink, setAwaitingEmailForLink] = useState(false);
  const [linkEmailPrompt, setLinkEmailPrompt] = useState("");
  const [linkEmailPromptError, setLinkEmailPromptError] = useState(false);
  const setAuthenticated = UserAuthStore((state) => state.setAuthenticated);
  const user = UserAuthStore((state) => state.user);
  const { theme } = useTheme(); // eslint-disable-line no-unused-vars
  const { showFeedback } = useFeedback();

  // Load configured SSO providers once AuthService is initialised.
  // getSsoProviders() returns [] before initialize() is called, so re-read
  // on mount (Secret.js runs initialize() before rendering Login).
  useEffect(() => {
    setSsoProviders(authService.getSsoProviders());
  }, []);

  const processAuthResult = async (firebaseUser) => {
    try {
      setLoading(true);
      
      // If a name was captured during magic link sign-up, apply it to the provider profile
      const pendingName = localStorage.getItem("nameForSignIn");
      if (pendingName && !firebaseUser.displayName) {
        await authService.updateProfile(firebaseUser, { displayName: pendingName });
        localStorage.removeItem("nameForSignIn");
      }

      // Get ID token (after profile update so name is in the token)
      const firebaseToken = await authService.getIdToken(firebaseUser, true);
      
      // Call backend login endpoint
      const { session_token, user_id, org_id } = await loginWithFirebase(firebaseToken);

      persistAuthSession({ sessionToken: session_token, userId: user_id, orgId: org_id });
      setAuthenticated(firebaseUser, org_id);
      
      // Check admin privileges
      await getModeAuth();
      
      return true;
    } catch (error) {
      logger.error('Login', 'Error processing authentication', { error: error.message });
      showFeedback(error.message || "Authentication failed during processing.", "error");
      await authService.signOut();
      setLoading(false);
      return false;
    }
  };

  const handleSsoSignIn = async (providerId) => {
    setLoading(true);
    try {
      // signInWithProvider returns null when a redirect is initiated (SAML / popup fallback).
      // In that case the page will reload after the redirect completes, so we just hold the
      // loading state. For popup success it returns the user directly.
      const firebaseUser = await authService.signInWithProvider(providerId);
      if (firebaseUser) {
        await processAuthResult(firebaseUser);
      }
    } catch (error) {
      if (error.code === "auth/popup-closed-by-user") {
        showFeedback("Sign-in was cancelled.", "info");
      } else {
        logger.error('Login', 'SSO sign-in failed', { providerId, error: error.message });
        showFeedback("Sign-in failed. Please try again.", "error");
      }
      setLoading(false);
    }
  };

  // Send passwordless email sign-in link
  const handleEmailLogin = async () => {
    setLinkSubmitted(true);
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailInput.trim());
    if (!nameInput.trim() || !emailInput.trim() || !emailValid) return;

    setLinkSending(true);
    try {
      await authService.sendSignInLinkToEmail(emailInput.trim(), nameInput.trim());
      localStorage.setItem("emailForSignIn", emailInput.trim());
      if (nameInput.trim()) {
        localStorage.setItem("nameForSignIn", nameInput.trim());
      }
      setEmailSent(true);
    } catch (error) {
      logger.error("Login", "Email link sign-in failed", { error: error.message });
      showFeedback(error.message || "Failed to send sign-in link.", "error");
    } finally {
      setLinkSending(false);
    }
  };

  // Forgot password — send reset email
  const handleForgotPassword = async () => {
    if (!emailInput.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailInput.trim())) {
      setResetEmailError(true);
      return;
    }
    setResetEmailError(false);
    try {
      await authService.sendPasswordResetEmail(emailInput.trim());
      setResetSent(true);
      showFeedback("Password reset email sent! Check your inbox.", "success");
    } catch (error) {
      logger.error("Login", "Password reset failed", { error: error.message });
      showFeedback(error.message || "Failed to send reset email.", "error");
    }
  };

  // Email/password sign-in
  const handleEmailPasswordLogin = async () => {
    if (!emailInput.trim() || !passwordInput.trim()) {
      showFeedback("Please enter both email and password.", "error");
      return;
    }

    try {
      setLoading(true);
      const user = await authService.signInWithEmailAndPassword(emailInput.trim(), passwordInput.trim());
      await processAuthResult(user);
    } catch (error) {
      logger.error("Login", "Email/password sign-in failed", { error: error.message });
      showFeedback(error.message || "Sign-in failed. Check your credentials.", "error");
      setLoading(false);
    }
  };

  const completeEmailLinkSignIn = (email) => {
    setLoading(true);
    authService.signInWithEmailLink(email, window.location.href)
      .then((user) => {
        localStorage.removeItem("emailForSignIn");
        // Clear the email link params from URL
        window.history.replaceState(null, "", window.location.origin);
        return processAuthResult(user);
      })
      .catch((error) => {
        logger.error("Login", "Email link verification failed", { error: error.message });
        showFeedback("Sign-in link expired or invalid. Please try again.", "error");
        setLoading(false);
      });
  };

  const handleLinkEmailPromptSubmit = () => {
    const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(linkEmailPrompt.trim());
    if (!valid) { setLinkEmailPromptError(true); return; }
    setLinkEmailPromptError(false);
    completeEmailLinkSignIn(linkEmailPrompt.trim());
  };

  // Handle return from email sign-in link
  useEffect(() => {
    if (!authService.isSignInWithEmailLink(window.location.href)) return;
    const email = localStorage.getItem("emailForSignIn");
    if (!email) {
      setAwaitingEmailForLink(true);
      return;
    }
    completeEmailLinkSignIn(email);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const location = useLocation();
  useEffect(() => {
    const requestedReturn = location.state?.from;
    if (typeof requestedReturn === "string" && requestedReturn.startsWith("/browser/authorize?")) {
      sessionStorage.setItem(BROWSER_AUTH_RETURN_KEY, requestedReturn);
    }
  }, [location.state]);
  // Only redirect if not already on a chat route with an id
  const isChatWithId =
    /^\/chat\/[\w-]+$/.test(location.pathname) ||
    /^\/spectra\/[\w-]+\/chat\/[\w-]+$/.test(location.pathname);
  const returnPath = location.state?.from || sessionStorage.getItem(BROWSER_AUTH_RETURN_KEY);
  const browserAuthorizationReturn =
    typeof returnPath === "string" && returnPath.startsWith("/browser/authorize?");
  if (user?.uid && browserAuthorizationReturn) return <Navigate to={returnPath} replace />;
  if (user?.uid && !isChatWithId) return <Navigate to={"/chat"} />;

  if (awaitingEmailForLink) {
    return (
      <AuthPageLayout>
        <div
          className="photon-content-card w-full max-w-80 rounded-lg p-5 dark:bg-surface-card bg-surface-card"
          style={{ boxShadow: "0px 1px 4px 0px #00000035" }}
        >
          <div className="text-center mb-4">
            <h2 className="text-sm font-semibold dark:text-ink-white text-ink-ink">Confirm your email</h2>
            <p className="text-xs text-ink-muted-control dark:text-ink-muted-text-dark mt-2">
              To finish signing in, please enter the email address you used to request this link.
            </p>
          </div>
          <input
            type="email"
            placeholder="Enter your email"
            value={linkEmailPrompt}
            onChange={(e) => { setLinkEmailPrompt(e.target.value); setLinkEmailPromptError(false); }}
            onKeyDown={(e) => e.key === "Enter" && handleLinkEmailPromptSubmit()}
            className={`photon-field w-full px-4 py-2 text-sm rounded-lg border ${
              linkEmailPromptError ? "photon-field-invalid border-edge-red-500" : ""
            }`}
          />
          {linkEmailPromptError && (
            <p className="text-[11px] text-ink-red-500 mt-1">Please enter a valid email address.</p>
          )}
          <button
            onClick={handleLinkEmailPromptSubmit}
            className="photon-primary-action mt-3 w-full px-4 py-2 text-sm font-medium rounded-full bg-button-primary text-button-on-primary transition-colors"
          >
            Continue
          </button>
        </div>
      </AuthPageLayout>
    );
  }

  return (
    <>
      {loading ? (
        <div
          className={`h-screen flex items-center justify-center dark:bg-surface-page bg-surface-page`}
        >
          <Loader />
        </div>
      ) : (
        <AuthPageLayout>
          <MessageAlert />
          <div className="flex flex-col items-center gap-6 mt-4">
              <OAuthButton providers={ssoProviders} onSignIn={handleSsoSignIn} />

              <div className="flex items-center w-full max-w-80 px-4">
                <div className="flex-1 border-t border-edge-gray-300 dark:border-edge-gray-600"></div>
                <span className="mx-3 text-xs text-ink-gray-500 dark:text-ink-gray-400">or</span>
                <div className="flex-1 border-t border-edge-gray-300 dark:border-edge-gray-600"></div>
              </div>

              {/* Toggle between passwordless and email/password */}
              <div role="group" aria-label="Sign-in method" className="photon-segments flex items-center w-full max-w-80 rounded-lg p-1 bg-surface-muted-strong dark:bg-surface-muted-strong">
                <button
                  aria-pressed={!usePasswordless}
                  onClick={() => {
                    setUsePasswordless(false);
                    setEmailSent(false);
                    setLinkSubmitted(false);
                  }}
                  className={`flex-1 py-1.5 text-sm rounded-full font-medium transition-all duration-200 ${
                    !usePasswordless
                      ? 'bg-fill-white dark:bg-fill-dialog-border text-ink-ink dark:text-ink-white font-semibold shadow-sm'
                      : 'text-ink-gray-500 dark:text-ink-gray-400 hover:text-ink-gray-700 dark:hover:text-ink-gray-200'
                  }`}
                >
                  Via Password
                </button>
                <button
                  aria-pressed={usePasswordless}
                  onClick={() => {
                    setUsePasswordless(true);
                    setPasswordInput("");
                  }}
                  className={`flex-1 py-1.5 text-sm rounded-full font-medium transition-all duration-200 ${
                    usePasswordless
                      ? 'bg-fill-white dark:bg-fill-dialog-border text-ink-ink dark:text-ink-white shadow-sm'
                      : 'text-ink-gray-500 dark:text-ink-gray-400 hover:text-ink-gray-700 dark:hover:text-ink-gray-200'
                  }`}
                >
                  Via Link
                </button>
              </div>

              {usePasswordless ? (
                emailSent ? (
                  <div className="flex flex-col items-center gap-3 text-center">
                    <AcceptedInviteIcon className="fill-ink-hex-00be07 w-16" />
                    <p className="text-sm text-ink-gray-700 dark:text-ink-gray-300 max-w-72">
                      Email sent! Check your inbox and click the link to sign in.
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-3 w-full max-w-80">
                    <input
                      type="text"
                      placeholder="Your full name"
                      value={nameInput}
                      onChange={(e) => setNameInput(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleEmailLogin()}
                      className={`photon-field w-full px-4 py-2 text-sm rounded-lg border ${
                        linkSubmitted && !nameInput.trim()
                          ? "photon-field-invalid border-edge-red-600 dark:border-edge-red-600"
                          : ""
                      }`}
                    />
                    <input
                      type="email"
                      placeholder="Enter your email"
                      value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleEmailLogin()}
                      className={`photon-field w-full px-4 py-2 text-sm rounded-lg border ${
                        linkSubmitted && (!emailInput.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailInput.trim()))
                          ? "photon-field-invalid border-edge-red-600 dark:border-edge-red-600"
                          : ""
                      }`}
                    />
                    <button
                      onClick={handleEmailLogin}
                      disabled={linkSending}
                      className="photon-primary-action w-full px-4 py-2 text-sm font-medium rounded-full bg-button-primary text-button-on-primary transition-colors disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {linkSending && (
                        <svg className="animate-spin h-4 w-4 text-ink-on-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                        </svg>
                      )}
                      {linkSending ? "Sending…" : "Send Sign-in Link"}
                    </button>
                  </div>
                )
              ) : (
                <div className="flex flex-col items-center gap-3 w-full max-w-80">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="Enter your email"
                    value={emailInput}
                    onChange={(e) => { setEmailInput(e.target.value); setResetEmailError(false); }}
                    className={`photon-field w-full px-4 py-2 text-sm rounded-lg border ${resetEmailError ? "photon-field-invalid border-edge-red-500" : ""}`}
                  />
                  <div className="relative w-full">
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={passwordInput}
                      onChange={(e) => setPasswordInput(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleEmailPasswordLogin()}
                      className="photon-field w-full px-4 py-2 pr-10 text-sm rounded-lg border"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-gray-400 hover:text-ink-gray-600 dark:hover:text-ink-gray-200"
                      tabIndex={-1}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <div className="w-full flex justify-end">
                    <button
                      type="button"
                      onClick={handleForgotPassword}
                      className="photon-text-link text-xs hover:underline"
                    >
                      {resetSent ? "Reset email sent" : "Forgot password?"}
                    </button>
                  </div>
                  <button
                    onClick={handleEmailPasswordLogin}
                    className="photon-primary-action w-full px-4 py-2 text-sm font-medium rounded-full bg-button-primary text-button-on-primary transition-colors"
                  >
                    Sign In
                  </button>
                </div>
              )}
          </div>
        </AuthPageLayout>
      )}
    </>
  );
};

export default Login;
